import { useState } from "react";
import { ArrowDown, ArrowUp, Columns3, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ATTRIBUTE_COLUMNS, DOMAIN_ONLY_ATTRIBUTES, METRICS_CONFIG, type MetricGroup } from "@/config/metrics";
import type { CustomMetric } from "@/lib/custom-metrics";
import { metricLabel } from "./metricDisplay";
import { cn } from "@/lib/utils";

interface MetricsFilterProps {
  selectedMetrics: string[];
  onMetricsChange: (metrics: string[]) => void;
  /** User-defined metrics, shown as their own "Custom" group. */
  customMetrics?: CustomMetric[];
  /** Whether a column has values under the current grouping (appliesToGrouping); the rest are greyed. */
  applies?: (id: string) => boolean;
  /** Switches the grouping to Domain, where every column has values. */
  onGroupByDomain?: () => void;
  /** What Reset puts back. */
  defaultColumns?: string[];
}

/** Why a column has no values under this grouping (appliesToGrouping's rules). */
const needs = (id: string): string =>
  DOMAIN_ONLY_ATTRIBUTES.has(id)
    ? "Shows when grouped by Domain"
    : id === "brand" || ATTRIBUTE_COLUMNS.has(id)
    ? "Shows when grouped by Domain or URL"
    : id === "campaign_group"
      ? "Shows unless grouped by Campaign Group"
      : id.startsWith("kv_")
        ? "Shows when grouped by Brand, Domain or URL"
        : "Shows when grouped by Domain only";

const groupBoxClass = "size-3.5 shrink-0 cursor-pointer accent-primary";

/**
 * "Columns": every column of the table, as the Columns list on Indexation
 * looks - native checkboxes, searchable, in groups with a sticky header and a
 * count (owner, 03.11.2026; docs/clicks-filter-buttons-variants.html 4A). Not
 * "Metrics": Brand and the Domain Tracker fields are not metrics. A column
 * without values under this grouping stays in the list, greyed with the
 * reason, and can still be ticked (4E) - hidden ones read as "gone". "Shown"
 * orders the picked ones with buttons, the non-drag way to what the header
 * drag does; both change the one order there is, selectedMetrics.
 */
export function MetricsFilter({
  selectedMetrics,
  onMetricsChange,
  customMetrics = [],
  applies = () => true,
  onGroupByDomain,
  defaultColumns,
}: MetricsFilterProps) {
  const [query, setQuery] = useState("");
  const [shownOnly, setShownOnly] = useState(false);

  // Custom metrics ride in the same picker as a trailing group so the grid and
  // "All"/"Clear" treat them exactly like built-ins — no separate control.
  const groups: MetricGroup[] = customMetrics.length
    ? [
        ...METRICS_CONFIG,
        {
          id: "custom",
          title: "Custom",
          description: "Your own formulas",
          metrics: customMetrics.map((m) => ({
            id: m.id,
            label: m.name,
            description: m.formula,
          })),
        },
      ]
    : METRICS_CONFIG;
  const labelOf = new Map(groups.flatMap((g) => g.metrics.map((m) => [m.id, metricLabel(m.id, m.label)] as const)));
  const selected = new Set(selectedMetrics);
  const q = query.trim().toLowerCase();
  const shownGroups = groups
    .map((g) => ({
      ...g,
      metrics: g.metrics.filter(
        (m) =>
          !q ||
          (labelOf.get(m.id) ?? m.label).toLowerCase().includes(q) ||
          m.id.includes(q) ||
          m.description?.toLowerCase().includes(q),
      ),
    }))
    .filter((g) => g.metrics.length);

  const toggle = (id: string) =>
    onMetricsChange(selected.has(id) ? selectedMetrics.filter((c) => c !== id) : [...selectedMetrics, id]);
  // The group's box ticks or clears the columns the search shows of it.
  const toggleGroup = (ids: string[]) =>
    onMetricsChange(
      ids.every((id) => selected.has(id))
        ? selectedMetrics.filter((c) => !ids.includes(c))
        : [...selectedMetrics, ...ids.filter((id) => !selected.has(id))],
    );
  const move = (i: number, by: -1 | 1) => {
    const next = [...selectedMetrics];
    [next[i], next[i + by]] = [next[i + by], next[i]];
    onMetricsChange(next);
  };
  const footerButton = "cursor-pointer text-xs font-medium text-muted-foreground hover:text-foreground disabled:cursor-default disabled:opacity-40";

  return (
    <Popover onOpenChange={(o) => (o ? setShownOnly(false) : setQuery(""))}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="cursor-pointer gap-2">
          <Columns3 className="h-4 w-4" aria-hidden />
          Columns
          <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-accent px-1 text-[11px] font-semibold text-accent-foreground tabular-nums">
            {selectedMetrics.length}
          </span>
        </Button>
      </PopoverTrigger>
      {/* Never taller than the room on screen: with 10 groups the list outgrew
          small windows and the bottom groups became unreachable (30.09.2026).
          rem, not px: at a 24px root font a px box squeezed its rem text. */}
      <PopoverContent
        align="end"
        collisionPadding={8}
        className="flex max-h-[var(--radix-popover-content-available-height)] w-[min(22rem,calc(100vw-1rem))] flex-col gap-2 p-2"
      >
        <div className="flex shrink-0 items-center gap-2">
          {shownOnly ? (
            <p className="flex-1 px-1 text-sm font-medium">Shown, in table order</p>
          ) : (
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Find a column…"
              aria-label="Find a column"
              className="h-8 min-w-0 flex-1 rounded-md border border-border bg-transparent px-2 text-sm outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
          )}
          <button
            type="button"
            onClick={() => setShownOnly((s) => !s)}
            aria-pressed={shownOnly}
            className="shrink-0 cursor-pointer rounded-md px-2 py-1 text-xs font-medium text-accent-foreground hover:bg-muted"
          >
            {shownOnly ? "All columns" : `Shown (${selectedMetrics.length}) ↕`}
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {shownOnly ? (
            selectedMetrics.length ? (
              <ol className="space-y-0.5">
                {selectedMetrics.map((id, i) => {
                  const label = labelOf.get(id) ?? metricLabel(id);
                  return (
                    <li key={id} className="flex items-center gap-1 rounded px-1 py-0.5 text-sm hover:bg-muted">
                      <span className={cn("min-w-0 flex-1 truncate", !applies(id) && "text-muted-foreground")} title={applies(id) ? label : `${label} - ${needs(id).toLowerCase()}`}>
                        {label}
                      </span>
                      <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label={`Move ${label} left`} className="inline-flex size-6 cursor-pointer items-center justify-center rounded text-muted-foreground hover:bg-background hover:text-foreground disabled:cursor-default disabled:opacity-30">
                        <ArrowUp className="size-3.5" aria-hidden />
                      </button>
                      <button type="button" onClick={() => move(i, 1)} disabled={i === selectedMetrics.length - 1} aria-label={`Move ${label} right`} className="inline-flex size-6 cursor-pointer items-center justify-center rounded text-muted-foreground hover:bg-background hover:text-foreground disabled:cursor-default disabled:opacity-30">
                        <ArrowDown className="size-3.5" aria-hidden />
                      </button>
                      <button type="button" onClick={() => toggle(id)} aria-label={`Hide ${label}`} className="inline-flex size-6 cursor-pointer items-center justify-center rounded text-muted-foreground hover:bg-background hover:text-foreground">
                        <X className="size-3.5" aria-hidden />
                      </button>
                    </li>
                  );
                })}
              </ol>
            ) : (
              <p className="px-1 py-2 text-xs text-muted-foreground">No columns shown.</p>
            )
          ) : shownGroups.length ? (
            shownGroups.map((group) => {
              const ids = group.metrics.map((m) => m.id);
              const all = ids.every((id) => selected.has(id));
              const some = ids.some((id) => selected.has(id));
              const whole = groups.find((g) => g.id === group.id)!.metrics;
              const off = ids.every((id) => !applies(id));
              return (
                <div key={group.id} role="group" aria-label={group.title}>
                  <label
                    className="sticky top-0 z-10 flex cursor-pointer items-center gap-2 border-b border-border bg-popover px-1 py-1.5 text-xs font-semibold"
                    title={group.description}
                  >
                    <input
                      type="checkbox"
                      className={groupBoxClass}
                      checked={all}
                      ref={(el) => {
                        if (el) el.indeterminate = some && !all;
                      }}
                      onChange={() => toggleGroup(ids)}
                    />
                    <span className={cn("flex-1", off && "text-muted-foreground")}>{group.title}</span>
                    <span className="font-normal text-muted-foreground tabular-nums">
                      {whole.filter((m) => selected.has(m.id)).length}/{whole.length}
                    </span>
                  </label>
                  {off ? (
                    <p className="px-1 pt-1 text-[11px] text-amber-700 dark:text-amber-300">
                      {needs(ids[0])}
                      {onGroupByDomain ? (
                        <>
                          {" · "}
                          <button type="button" onClick={onGroupByDomain} className="cursor-pointer text-accent-foreground underline-offset-2 hover:underline">
                            Group by Domain
                          </button>
                        </>
                      ) : null}
                    </p>
                  ) : null}
                  <div className="py-0.5">
                    {group.metrics.map((metric) => {
                      const ok = applies(metric.id);
                      return (
                        <label
                          key={metric.id}
                          className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-muted"
                          title={[metric.description, ok ? "" : needs(metric.id)].filter(Boolean).join(" - ")}
                        >
                          <input
                            type="checkbox"
                            className={groupBoxClass}
                            checked={selected.has(metric.id)}
                            onChange={() => toggle(metric.id)}
                          />
                          <span className={cn("min-w-0 flex-1 truncate", !ok && "text-muted-foreground")}>
                            {labelOf.get(metric.id) ?? metric.label}
                          </span>
                          {!ok && !off ? <span className="shrink-0 text-[11px] text-muted-foreground">not here</span> : null}
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })
          ) : (
            <p className="px-1 py-2 text-xs text-muted-foreground">No such column</p>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-3 border-t border-border px-1 pt-2">
          <button type="button" onClick={() => onMetricsChange(groups.flatMap((g) => g.metrics.map((m) => m.id)))} className={footerButton}>
            All
          </button>
          <button type="button" onClick={() => onMetricsChange([])} disabled={!selectedMetrics.length} className={footerButton}>
            Clear
          </button>
          {defaultColumns ? (
            <button type="button" onClick={() => onMetricsChange(defaultColumns)} className={footerButton}>
              Reset
            </button>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}
